// Ops-only directory evidence and a separate, explicitly reviewed association operation.
import React, { useState } from "react";
import type { Dealer, NsDirectoryStatus, NsResellerAssociationRequest, NsResellerAssociationResult } from "@phoneware/lens-domain";
import { ApiError } from "../api.js";
import { errorText } from "../format.js";
import { useSession } from "../session.js";
import { Callout, Sheet } from "../ui.js";

export function DirectoryTime({ value }: { value: string }): React.ReactElement {
  return <time dateTime={value}>{new Date(value).toLocaleString("en-US", { timeZone: "UTC", year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })} UTC</time>;
}

export function NsIdentity({ dealer }: { dealer: Dealer }): React.ReactElement {
  return (
    <span className="ns-identity" data-testid={`dealer-ns-${dealer.id}`}>
      {dealer.nsReseller === null ? <span className="muted">Omni-only</span> : <>
        <code className="ns-key">{dealer.nsReseller}</code>
        <span className="ns-stamp">Last verified: {dealer.nsResellerVerifiedAt ? <DirectoryTime value={dealer.nsResellerVerifiedAt} /> : "Never verified"}</span>
      </>}
    </span>
  );
}

const ISSUE_WORD: Record<NsDirectoryStatus["issues"][number]["kind"], string> = {
  missing: "Missing dealer",
  duplicate: "Duplicate association",
  unverified: "Unverified association",
  unknown_reseller: "Absent from the last verified directory",
  invalid_claim: "Association integrity conflict",
};

export function NsDirectoryPanel({ status, error, busy, onSync, onReload, dealers, onReview }: {
  status: NsDirectoryStatus | null;
  error: string | null;
  busy: boolean;
  /** null for a tier that reads the directory without changing dealers (dealers.manage). */
  onSync: (() => void) | null;
  onReload: () => void;
  dealers: Dealer[];
  onReview: ((id: string) => void) | null;
}): React.ReactElement {
  const directory = status?.directory;
  const failed = status?.lastAttempt?.error;
  const covered = status?.entries.filter((entry) => entry.verifiedDealerId !== null && !status.issues.some((issue) => issue.reseller === entry.reseller && issue.kind !== "unknown_reseller")).length ?? 0;
  return (
    <section className="card ns-directory" aria-labelledby="ns-directory-title" data-testid="ns-directory-status" data-source-state={failed ? "failed" : directory ? "verified" : "unknown"}>
      <div className="ns-directory-h">
        <h2 id="ns-directory-title">NetSapiens directory</h2>
        <div className="ns-actions">
          <button className="btn" type="button" disabled={busy} data-testid="ns-status-refresh" onClick={onReload}>Refresh status</button>
          {onSync ? <button className="btn" type="button" disabled={busy} data-testid="ns-sync" onClick={onSync}>{busy ? "Checking" : "Sync NS dealers"}</button> : null}
        </div>
      </div>
      <p className="ns-summary" data-testid="ns-directory-summary">{directory ? `${covered} of ${directory.total} resellers have a verified dealer` : "No verified NS directory yet."}</p>
      {directory ? <p className="ns-stamp">Directory verified <DirectoryTime value={directory.verifiedAt} />{failed ? ". Showing the last verified directory." : null}</p> : <p className="ns-stamp">Coverage is unknown until a complete directory read succeeds.</p>}
      {status?.lastAttempt ? <p className="ns-stamp">{failed ? "Latest attempt failed" : "Latest attempt finished"}: <DirectoryTime value={status.lastAttempt.finishedAt} /></p> : null}
      {failed ? <Callout kind="warn" icon="triangle-alert"><span data-testid="ns-source-error">{failed} Existing dealers are retained.</span></Callout> : null}
      {error && error !== failed ? <Callout kind="stop" icon="triangle-alert"><span data-testid="ns-operation-error">{error}</span></Callout> : null}
      {status?.issues.length ? <details className="ns-issues" open>
        <summary>{status.issues.length} association {status.issues.length === 1 ? "issue" : "issues"}</summary>
        <ul className="rows">
          {status.issues.map((issue) => <li className="ns-issue" key={`${issue.kind}:${issue.reseller}`}>
            <div className="ns-issue-main"><code className="ns-key">{issue.reseller}</code><span>{ISSUE_WORD[issue.kind]}</span>
              {issue.dealerIds.map((id) => <span className="ns-holder" key={id}>{dealers.find((d) => d.id === id)?.name ?? "Dealer record unavailable"}<code className="ns-id">{id}</code></span>)}
            </div>
            {onReview && issue.dealerIds.some((id) => dealers.some((d) => d.id === id)) ? <button className="btn" type="button" onClick={() => onReview(issue.dealerIds.find((id) => dealers.some((d) => d.id === id))!)}>Review</button> : null}
          </li>)}
        </ul>
      </details> : null}
      <p className="card-note">Omni-only dealers remain valid. Directory presence grants no user access.</p>
    </section>
  );
}

interface Review {
  input: NsResellerAssociationRequest;
  key: string;
  holders: Array<{ id: string; name: string }>;
}

export function NsAssociationSheet({ dealer, dealers, status, onReload, onChanged, onClose }: {
  dealer: Dealer;
  dealers: Dealer[];
  status: NsDirectoryStatus | null;
  onReload: () => Promise<void>;
  onChanged: (result: NsResellerAssociationResult) => Promise<void>;
  onClose: () => void;
}): React.ReactElement {
  const { api } = useSession();
  const [selected, setSelected] = useState(dealer.nsReseller ?? "");
  const [review, setReview] = useState<Review | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const directory = status?.directory;
  const unknown = dealer.nsReseller !== null && !!directory && !directory.resellers.includes(dealer.nsReseller);
  const canSelect = !!directory && selected !== "" && directory.resellers.includes(selected) && (dealer.nsReseller === null || dealer.nsReseller === selected);

  function prepare(clear = false): void {
    if ((!canSelect && !clear) || (clear && !unknown)) return;
    const key = clear ? dealer.nsReseller! : selected;
    const entry = status?.entries.find((e) => e.reseller === key);
    const ids = clear ? [dealer.id] : [...new Set([...(entry?.dealerIds ?? []), ...(entry?.verifiedDealerId ? [entry.verifiedDealerId] : [])])];
    setReview({
      key,
      input: clear ? { reseller: null, expectedReseller: key } : { reseller: key, expectedDealerIds: ids },
      holders: ids.map((id) => ({ id, name: dealers.find((d) => d.id === id)?.name ?? "Dealer record unavailable" })),
    });
    setAcknowledged(false);
    setError(null);
  }

  async function confirm(): Promise<void> {
    if (!review || !acknowledged || busy) return;
    setBusy(true);
    setError(null);
    let result: NsResellerAssociationResult;
    try {
      result = await api.setNsReseller(dealer.id, review.input);
    } catch (err) {
      // The reviewed holder set is discarded on every refusal. A 409 must never be replayed automatically.
      setReview(null);
      setAcknowledged(false);
      setError(errorText(err));
      try {
        await onReload();
        if (err instanceof ApiError && err.status === 409) setError(`${errorText(err)} Dealer and directory data refreshed. Review every affected dealer again.`);
      } catch (refreshError) {
        setError(`${errorText(err)} Refresh failed: ${errorText(refreshError)}`);
      }
      setBusy(false);
      return;
    }
    try {
      await onChanged(result);
      onClose();
    } catch (err) {
      // The write succeeded. Do not offer the same confirmation again if readback failed.
      setReview(null);
      setAcknowledged(false);
      setError(`Association saved. Refresh failed: ${errorText(err)} Refresh status before reviewing another change.`);
      setBusy(false);
    }
  }

  return (
    <Sheet title={review ? "Review NS association" : "NS association"} onClose={busy ? () => undefined : onClose} foot={<>
      <button className="btn" type="button" disabled={busy} onClick={review ? () => { setReview(null); setAcknowledged(false); } : onClose}>{review ? "Back" : "Cancel"}</button>
      {review ? <button className="btn primary" type="button" data-testid="ns-association-confirm" disabled={busy || !acknowledged} onClick={() => void confirm()}>{busy ? "Saving" : "Confirm association"}</button> : unknown ?
        <button className="btn primary" type="button" data-testid="ns-association-clear" disabled={busy} onClick={() => prepare(true)}>Review clearing association</button> :
        <button className="btn primary" type="button" data-testid="ns-association-review" disabled={!canSelect || busy} onClick={() => prepare()}>Review association</button>}
    </>}>
      <div className="stack ns-association">
        <div><b>{dealer.name}</b><code className="ns-id">{dealer.id}</code></div>
        {error ? <Callout kind="stop" icon="triangle-alert"><span data-testid="ns-association-error">{error}</span></Callout> : null}
        {review ? <>
          <dl className="facts"><div><dt>Exact NS reseller key</dt><dd><code className="ns-key">{review.key}</code></dd></div><div><dt>{review.input.reseller === null ? "Clear association from" : "Assign association to"}</dt><dd>{dealer.name}<code className="ns-id">{dealer.id}</code></dd></div></dl>
          <div><h3>Current holders</h3>{review.holders.length ? <ul className="rows ns-review-holders" data-testid="ns-reviewed-holders">{review.holders.map((holder) => <li key={holder.id}>
            <b>{holder.name}</b><code className="ns-id">{holder.id}</code><span className="row-m">{review.input.reseller !== null && holder.id === dealer.id ? "Keeps this association" : "Becomes Omni-only"}</span>
          </li>)}</ul> : <p>No current holders.</p>}</div>
          <Callout kind="warn" icon="triangle-alert">Only the NS association moves. Users, customers, quotes and orders stay with their dealer.</Callout>
          <label className="ns-ack"><input type="checkbox" data-testid="ns-association-ack" checked={acknowledged} disabled={busy} onChange={(e) => setAcknowledged(e.target.checked)} /><span>I reviewed every affected dealer.</span></label>
        </> : <>
          <NsIdentity dealer={dealer} />
          {!directory ? <Callout kind="warn" icon="triangle-alert">A verified directory is required. Close this sheet and sync NS dealers.</Callout> : <>
            <p className="field-note">Directory verified <DirectoryTime value={directory.verifiedAt} />. Assignment verifies a fresh directory before saving.</p>
            {status?.lastAttempt?.error ? <Callout kind="warn" icon="triangle-alert">Latest attempt failed: {status.lastAttempt.error} The displayed directory is the last verified observation.</Callout> : null}
            {unknown ? <Callout kind="warn" icon="triangle-alert">This exact key is absent from the last verified directory. Clear only this association before selecting another key. The server checks a fresh directory before clearing it.</Callout> : <label className="field"><span>Exact NS reseller key</span>
              <select className="input ns-key" data-testid="ns-reseller-select" value={selected} onChange={(e) => { setSelected(e.target.value); setError(null); }}>
                <option value="">Select an NS key</option>
                {directory.resellers.map((key) => <option key={key} value={key} disabled={dealer.nsReseller !== null && dealer.nsReseller !== key}>{key}</option>)}
              </select>
            </label>}
            {dealer.nsReseller !== null && !unknown ? <p className="field-note">To move this key, open the destination dealer and select it there. A different key cannot replace this association.</p> : null}
          </>}
          <p className="field-note">Only the NS association moves. Users, customers, quotes and orders stay with their dealer.</p>
        </>}
      </div>
    </Sheet>
  );
}
